import angular from 'angular';

import EditInvoiceModalHtml       from './templates/edit.invoice.modal.html';
import EmailInvoiceHtml           from './invoice.email.html';
import InvoiceItemDiscountHtml    from './templates/invoice.item-discount.html';
import InvoiceNewRenewalDatesHtml from './invoice.new-renewal-dates.html';
import InvoiceReworkHtml          from './invoice.rework.html';
import InvoicesActivityHtml       from './templates/invoices.activity.html';
import InvoiceTradeInModalHtml    from './templates/invoice-trade-in.modal.html';
import InvoiceUnitCostHtml        from './invoice.unit-cost.html';
import InvoiceUnitCreditModalHtml from './templates/invoice-unit-credit.modal.html';
import InvoiceUnitSaleModalHtml   from './templates/invoice-unit-sale.modal.html';
import OrderingFromInvoiceHtml    from './invoice.ordering-from-invoice.html';
import SplitInvoiceHtml           from './invoice.split.html';
import WindowFlipHtml             from '../../app-lite/core/templates/window-flip.html';

export function InvoiceService (
  $q,
  $state,
  $translate,
  $uibModal,
  $window,
  API,
  bigDecimalApi,
  cashInvoiceApi,
  companyApi,
  confirm,
  Customer,
  dateParsing,
  Deposit,
  InvoiceTotals,
  jsonApi,
  messages,
  pruneApi,
  sessionObjAPI,
  Tiremetrix,
  vehicleApi,
  workshopAPI
) {

  const _defaultInvoiceItem = {
    amount                : 0.00,
    description           : '',
    gst                   : 0.00,
    gst_free              : false,
    hours_worked          : 0,
    id                    : '',
    invoice_id            : '',
    item_code             : '',
    job_card_note         : '',
    note                  : '',
    product_id            : '',
    product_type          : 'W',
    quantity              : 0,
    requires_serial_number: false,
    unit_cost             : 0.00,
    unit_price            : 0.00,
    untouched             : true
  };

  var Invoice = function (invoice, taxGroups = []) {
    this.currency   = sessionObjAPI.currency();
    this.deposit    = new Deposit(invoice);
    this.form       = {};
    this.invoice    = invoice;
    this.taxGroups  = taxGroups;
    this.totals     = new InvoiceTotals(invoice);

    this.Tiremetrix = new Tiremetrix();
  };

  /**
   * Confirms non cash invoices before processing.
   * Either asks to update the post date to today
   * or simply asks if they are sure they want to process.
   * @param  {object} invoice The invoice to be processed
   * @return {promise}        The resolved or rejected promise
   */
  var dateCheck = function (invoice, options) {
    return $q.when()
    .then(() => {
      if (dateParsing.isToday(invoice.post_date) || companyApi.useTodayForPostDate()) {
        return companyApi.useTodayForPostDate() ? true : $q.when();
      }

      return confirm.question($translate.instant('JS_SPACE.CONFIRM.UPDATE_POST_DATE_TO_TODAY'));
    })
    .then(updatePostDate => {
      if (angular.isDefined(updatePostDate) && updatePostDate) {
        invoice.post_date = dateParsing.today();
      }

      return options && options.skipConfirm ?
        $q.when() :
        confirm.generic(options && options.isSale ? $translate.instant('JS_SPACE.CONFIRM.SAVE_PROCESS_SALE') : $translate.instant('JS_SPACE.CONFIRM.SAVE_PROCESS_INVOICE'));
    })
    .then(() => invoice);
  };

  var isEmpty = function (obj) {
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }

    return true;
  };

  /**
   * Determines if the invoice is a cash invoice
   * or account invoice
   * @param  {string} type    'cash' or 'account'
   * @return {boolean}        Is the invoice the specified account type?
   */
  Invoice.prototype.account = function (type) {
    var self = this;
    var typeMap = {
      cash   : 'C',
      account: 'A',
    };

    return typeMap[type] === self.invoice.cash_or_account;
  };

  Invoice.prototype.addWofPlateRenewalDates = function () {
    const self = this;

    return $uibModal.open({
      controller  : 'NewRenewalDatesCtrl',
      controllerAs: '$ctrl',
      templateUrl : InvoiceNewRenewalDatesHtml,
      windowClass : 'md-modal',
      resolve     : {
        Invoice: () => self,
        vehicle: () => workshopAPI.get('/vehicle', self.invoice.vehicle_id)
      }
    }).result;
  };

  /**
   * Before we do any saves, the array of items to delete
   * needs to be added to the list of items. Also, we need
   * to remove all untouched items
   * @param  {object} invoice Invoice to prepare for save
   * @return {object}         Assembled invoice
   */
  Invoice.prototype.assemble = function () {
    const self = this;

    var invoice = jsonApi.copy(self.invoice);

    invoice.invoice_items_attributes     = pruneApi.removeUntouched(invoice.invoice_items_attributes);
    invoice.invoice_items_attributes     = invoice.invoice_items_attributes.concat(invoice.itemsToDelete);
    invoice.customer_deposits_attributes = invoice.customer_deposits_attributes.concat(invoice.depositsToDelete);

    return $q.when()
    .then(() => ( !self.taxGroups || !self.taxGroups.length )
      ? workshopAPI.get('/tax_groups')
      .then(taxGroups => {
        self.taxGroups = taxGroups;

        return invoice;
      })
      : $q.when(invoice)
    )
    .then(invoice => {
      if (invoice.tax_group_id) {
        invoice.tax_group_name = self.taxGroups.length
          ? self.taxGroups.filter(group => group.id === invoice.tax_group_id)[0].name
          : invoice.tax_group_name;
      }

      return $q.when(invoice);
    });
  };

  // Removes any empty line items from
  // the invoice.
  Invoice.prototype.cleanLineItems = function () {
    var self = this;

    for (var i=0; i<self.invoice.invoice_items_attributes.length; i++) {
      if (!self.invoice.invoice_items_attributes[i].item_code) {
        self.invoice.invoice_items_attributes.splice(i, 1);
      }
    }
  };

  /**
   * After all saves, certain actions need to take place no matter what.
   * They are handled here
   * @param  {object} invoice newly saved invoice
   * @return {promise}         The new invoice
   */
  Invoice.prototype.cleanup = function (invoice, options = {}) {
    var self = this;
    var deferred = $q.defer();

    // Set the invoice in the class to the new invoice
    self.invoice = invoice;

    // Set the form to pristine so page changes can happen without warning
    isEmpty(self.form) ? null : self.form.$setPristine();

    if (!options.skipRefresh) {

      // Redirect to page with new id from invoice
      $state.go('app.invoice', {
        customer_id: invoice.customer_id,
        vehicle_id : invoice.vehicle_id,
        id         : invoice.id
      }, { reload: true });
    }

    deferred.resolve(invoice);

    return deferred.promise;
  };

  Invoice.prototype.closed = function () {
    var self = this;

    return self.invoice.invoice_status !== 'O';
  };

  /**
   * Determines if the invoice discount type matches
   * the specified type
   * @param  {string} type The type of discount being checked
   * @return {boolean}     Is the invoice discount type matches the specified type
   */
  Invoice.prototype.discountType = function(type) {
    const self    = this;
    const typeMap = {
      cash      : 'C',
      itemized  : 'I',
      percentage: 'P'
    };

    return typeMap[type] === self.invoice.discount_type;
  };

  Invoice.prototype.editInvoice = function () {
    var self = this;

    return $uibModal.open({
      templateUrl: EditInvoiceModalHtml,
      controller : 'EditInvoiceCtrl',
      resolve    : {
        invoice: function () {
          return self.invoice;
        }
      },
      windowClass: 'lg-modal'
    }).result;
  };

  Invoice.prototype.email = function (invoice, options = {}) {
    return $uibModal.open({
      controller       : 'EmailInvoiceCtrl',
      templateUrl      : EmailInvoiceHtml,
      windowClass      : 'lg-modal flip-container',
      windowTemplateUrl: WindowFlipHtml,
      resolve          : {
        attachments    : () => options.attachments || [],
        companyMessages: () => companyApi.getDefaultMessages(),
        customer       : () => options.customer,
        inspection     : () => workshopAPI.get('/inspection/inspection_for_invoice', invoice.id),
        invoice        : () => invoice,
        isSale         : () => options.isSale,
        requestPayment : () => options.requestPayment,
        sendInspection : () => options.sendInspection,
        sendInvoice    : () => options.sendInvoice,
        type () {
          switch (invoice.invoice_type) {
            case 'I':
              return $translate.instant('INVOICE_TYPES.INVOICE');
            case 'Q':
              return $translate.instant('INVOICE_TYPES.QUOTE');
            case 'C':
              return $translate.instant('INVOICE_TYPES.CREDIT');
            default:
              return $translate.instant('INVOICE_TYPES.INVOICE');
          }
        }
      }
    }).result;
  };

  Invoice.prototype.getActivity = function (row) {
    var self = this;

    return $uibModal.open({
      controller: 'InvoiceActivityCtrl',
      resolve   : {
        activity: function () {
          return workshopAPI.get('/invoice_activity_logs/get_invoice_activity_information', row.id);
        }
      },
      templateUrl: InvoicesActivityHtml,
      windowClass: 'lg-modal'
    }).result;
  };

  Invoice.getPaymentTermFromCustomer = function ( customer = {} ) {
    const company     = sessionObjAPI.company();
    const customerApi = new Customer (customer);

    return customerApi.hasPaymentTerm()
      ? customer.default_payment_terms
      : company.default_payment_terms;
  };

  Invoice.prototype.getCopy = function () {
    var self = this;
    var copy = sessionObjAPI.get('invoice_copy');

    if (copy) {
      self.invoice     = copy;
      self.totals      = new InvoiceTotals(copy);
      self.deposit     = new Deposit(copy);
      self.form        = {};

      sessionObjAPI.removeKey('invoice_copy');
    }

    return copy ? copy : null;
  };

  Invoice.prototype.getCopyUrl = function (invoice) {
    return invoice.vehicle_id ? sprintf('%1$s/%2$s/invoice', invoice.customer_id, invoice.vehicle_id) : sprintf('%1$s/invoice', invoice.customer_id);
  };

  Invoice.getInitialInvoice = function (customer, customerSources) {
    return {
      balance_due                 : 0.00,
      cash_or_account             : customer.cash_or_account || 'C',
      cost                        : 0.00,
      customer_deposits_attributes: [],
      customer_id                 : customer.id || '',
      customer_source             : this.getSourceFromCustomer(customer, customerSources),
      depositsToDelete            : [],
      description                 : '',
      discount_type               : 'C',
      discount                    : 0.00,
      freight                     : 0.00,
      gst                         : 0.00,
      id                          : '',
      invoice_items_attributes    : [],
      invoice_number              : '',
      invoice_payment_terms       : this.getPaymentTermFromCustomer(customer),
      invoice_status              : 'O',
      invoice_tax_items_attributes: [],
      invoice_type                : 'I',
      itemsToDelete               : [],
      job_card_note               : '',
      job_status_comment          : '',
      job_status                  : 'B',
      note                        : '',
      odometer                    : 0,
      paid                        : 0.00,
      post_date                   : dateParsing.today(),
      rounding                    : '0.00',
      savedBalance                : '0',
      savedTotal                  : '0',
      subtotal                    : 0.00,
      tax_free                    : customer.tax_free,
      total                       : 0.00,
      vehicle_hours               : 0,
      work_in_progress            : false,

      // If the invoice has repco items
      repco: false,

      // If the invoice has ashdown items
      ashdown: false,

      // If the invoice has prolink items
      prolink: false,

      // If the invoice has burson items
      bursons: false,

      // If the invoice has oscar items
      oscar: false
    };
  };

  Invoice.prototype.getInitialInvoiceItem = function () {
    return angular.copy(_defaultInvoiceItem);
  };

  Invoice.prototype.getOriginalInvoiceUrl = function (property) {
    var self = this;

    return workshopAPI.get('/invoice/url_ids', self.invoice[property])
      .then(function (data) {
        return {
          customer_id: data.customer_id,
          vehicle_id : data.vehicle_id,
          id         : self.invoice[property]
        };
      }, function (err) {
        return $q.reject(err);
      });
  };

  /**
   * The customer source field on the customer object saves the code
   * from the source. In the invoice, it is the id. So here to get
   * the default, we have to map accordingly.
   *
   * @param   {any}           customer        - the customer for the invoice
   * @param   {Array<any>}    customerSources - list of customer sources
   * @returns {string | null}                 - id of matching source. Null if no match.
   */
  Invoice.getSourceFromCustomer = function (customer, customerSources = []) {
    const source = customerSources.filter(customerSource => customerSource.code === customer.customer_source)[0];

    return source ? source.id : null;
  };

  Invoice.prototype.getSplitInvoiceUrl = function () {
    var self = this;

    return workshopAPI.get('/invoice/url_ids', self.invoice.split_to_invoice_id)
      .then(function (data) {
        return {
          customer_id: data.customer_id,
          vehicle_id : data.vehicle_id,
          id         : self.invoice.split_to_invoice_id
        };
      }, function (err) {
        return $q.reject(err);
      });
  };

  Invoice.prototype.handleItemDiscount = function (index) {
    return $uibModal.open({
      controller : 'InvoiceItemDiscountCtrl',
      templateUrl: InvoiceItemDiscountHtml,
      windowClass: 'lg-modal',
      resolve    : {
        currency: () => this.currency,
        item    : () => {
          return { ...this.invoice.invoice_items_attributes[index] };
        }
      }
    }).result;
  };

  Invoice.prototype.isFromOutsideCompany = function () {
    var self = this;

    return !!self.invoice.outside_company;
  };

  /**
   * Will return boolean depending on whether or not an
   * individual product is discounted. Will check the
   * given product or all invoice products.
   * @param {object} product An invoice product object (optional).
   * @return {boolean}
   */
  Invoice.prototype.isItemizedDiscount = function (product) {
    let flag = false;

    if (product) {
      flag = !!product.discount;
    }
    else {
      this.invoice.invoice_items_attributes.forEach(product => {
        if (!!product.discount) {
          flag = true;
        }
      });
    }


    return flag;
  };

  // Modal used to find a unit (vehicle) for an invoice of
  // type credit.
  Invoice.prototype.launchUnitCreditModal = function () {
    var self = this;

    return $uibModal.open({
      templateUrl: InvoiceUnitCreditModalHtml,
      windowClass: 'trade-in-modal',
      controller : 'UnitCreditCtrl',
      resolve    : {
        units: [ 'workshopAPI', function (workshopAPI) {
          return workshopAPI.get('/vehicle/find_sold_to_customer', self.invoice.customer_id);
        }]
      }
    }).result;
  };

  // Modal used to find a unit (vehicle) for non-credit
  // type invoice.
  Invoice.prototype.launchUnitSaleModal = function () {
    return $uibModal.open({
      templateUrl: InvoiceUnitSaleModalHtml,
      windowClass: 'x-lg-modal',
      controller : 'UnitSaleCtrl',
      resolve    : {
        units: [ 'workshopAPI', function (workshopAPI) {
          return workshopAPI.post('/vehicles/for_sale_index', {
            offset     : 0,
            page_size  : 11,
            sale_status: 'for_sale'
          });
        }]
      }
    }).result;
  };

  // Modal used to collect information about a unit (vehicle)
  // being traded in.
  Invoice.prototype.launchUnitTradeInModal = function () {
    return $uibModal.open({
      templateUrl: InvoiceTradeInModalHtml,
      windowClass: 'product-search-modal',
      controller : 'TradeInCtrl',
      resolve    : {
        bodyTypes: [ 'workshopAPI', function (workshopAPI) {
          return workshopAPI.get('/system/vehicle_body_types/', null, 'bodyTypes');
        }],
        driveTypes: [ 'workshopAPI', function(workshopAPI) {
          return workshopAPI.get('/system/drive_types/', null, 'driveTypes');
        }],
        franchises: [ 'workshopAPI', function (workshopAPI) {
          return workshopAPI.get('/vehicle_group_or_franchise_types');
        }],
        fuelTypes: [ 'workshopAPI', function (workshopAPI) {
          return workshopAPI.get('/system/fuel_types/', null, 'fuelTypes');
        }],
        transmissionTypes: [ 'workshopAPI', function (workshopAPI) {
          return workshopAPI.get('/system/transmission_types/', null, 'transmissionTypes');
        }]
      }
    }).result;
  };

  Invoice.prototype.logActivity = function (type) {
    var self = this;

    return workshopAPI.post('invoice_activity_logs/log_invoice_activity', {invoice: self.invoice.id, type: type}, 'invoice');
  };

  Invoice.prototype.openReport = function (version, id, includeBurson) {
    $window.open(
      includeBurson || includeBurson === false ?
        `${API.url}/reports/${version}/${id}/${includeBurson}.pdf` :
        `${API.url}/reports/${version}/${id}.pdf`
    , '_blank');
  };

  Invoice.prototype.printVehicleForm = function(vehicle) {
    const self = this;

    return vehicleApi.getFormSettings(vehicle).then(settings => {
      workshopAPI.get('/vehicle_form_serial_numbers/check_if_exists', [settings.vehicle.id, settings.form_code]).then(serial_number => {
        if (serial_number !== "null") {
          if (serial_number.invoice_id && serial_number.invoice_id === self.invoice.id) {
            $window.open(`${API.url}/reports/jasper_vehicle_forms/${settings.vehicle.id}/${settings.form_code}.pdf`, '_blank');
          }
          else if (!serial_number.invoice_id) {
            workshopAPI.patch('/vehicle_form_serial_numbers/attach_invoice_to_existing_form', {
              invoice_id      : self.invoice.id,
              serial_number_id: serial_number.id
            }, 'vehicle_form_serial_number').then(() => {
              $window.open(`${API.url}/reports/jasper_vehicle_forms/${settings.vehicle.id}/${settings.form_code}.pdf`, '_blank');
            });
          }
          else {
            messages.show($translate.instant('JS_SPACE.MESSAGES.FORM_NOT_FOR_VEHICLE'), 'error');
          }
        }
          else {
            workshopAPI.post('/vehicle_form_serial_numbers', {
              vehicle_id: settings.vehicle.id,
              invoice_id: self.invoice.id,
              form_code : settings.form_code
            }, 'vehicle_form_serial_number').then(() => {
              $window.open(`${API.url}/reports/jasper_vehicle_forms/${settings.vehicle.id}/${settings.form_code}.pdf`, '_blank');
            });
          }
        });
      return;
    });
  };

  /**
   * Saves and processes the invoice after a confirmation,
   * validation, removing untouched items and adding in the
   * items to be deleted.
   * @return {promise} The newly processed invoice or the error
   */
  Invoice.prototype.process = function (options) {
    const self    = this;
    const company = sessionObjAPI.company();

    return $q.when()
    .then(() => self.invoice.vehicle_id && company.company_settings.ask_for_due_dates_on_process ?
      self.addWofPlateRenewalDates() :
      $q.when(self.invoice)
    )
    .then(invoice => dateCheck(invoice, options))
    .then(invoice => {
      self.invoice = invoice;

      return invoice;
    })
    .then(() => self.validate())
    .then(() => self.assemble())
    .then(invoice => workshopAPI.patch(
      'invoice/process_and_save',
      invoice,
      'invoice'
    ))
    .then(invoice => self.cleanup(invoice, options))
    .then(invoice => {
      return self.account('cash') && (invoice.total !== invoice.deposits_total) ?
        cashInvoiceApi.process(invoice, options) :
        $q.when(invoice);
    })
    .then(invoice => {
      return company.tiremetrix_settings.has_tiremetrix_tire_registration ?
        workshopAPI.get('/vehicle_tyre_registrations/registered_tyres_list_for_invoice', self.invoice.id)
        .then(tires => tires.length ? self.Tiremetrix.registerTires(invoice) : $q.when(invoice)) :
        $q.when(invoice);
    });
  };

  Invoice.prototype.productListForOrdering = function(invoice) {
    var self = this;

    $uibModal.open({
      templateUrl: OrderingFromInvoiceHtml,
      controller : 'orderingFromInvoiceCtrl',
      windowClass: 'x-lg-modal',
      resolve    : {
        invoice () {
          return invoice;
        },
        items: function () {
          return workshopAPI.get('/invoice_items/products_from_invoice_for_ordering', self.invoice.id);
        }
      }
    }).result.then(function(result){
      return workshopAPI.post("/purchase_orders/create_order_from_invoice", result, 'purchase_order');
    }).then(function() {
      $state.reload();
      messages.show($translate.instant('PURCHASE_ORDER_SPACE.ITEMS_ADDED_TO_ORDERS'), 'success');
    });
  };

  Invoice.prototype.replaceInvoiceItem = function ( item = {}, index = 0 ) {
    var replacedItem = this.getInitialInvoiceItem();

    /**
     * If a row is already part of a bundle, we want to maintain that.
     * Also, maintain the order.
     **/
    replacedItem.bom_parent_id = item.bom_parent_id;
    replacedItem.ordering      = item.ordering;

    this.invoice.invoice_items_attributes.splice(index, 0, replacedItem);
  };

  Invoice.prototype.rework = function () {
    const self = this;

    return $uibModal.open({
      controller : 'InvoiceReworkCtrl',
      templateUrl: InvoiceReworkHtml,
      resolve    : {
        invoice : () => self.invoice,
        invoices: () => {
          return self.invoice.rework_for_id
            ? []
            : workshopAPI.get('/invoices/reworkable_list', [ self.invoice.customer_id, self.invoice.vehicle_id, 0, 10 ]);
        }
      },
      windowClass: 'lg-modal'
    }).result;
  };

  /**
   * Saves the invoice after removing untouched items,
   * adding the items to be deleted and validating it.
   * @return {promise} The newly saved invoice or the error
   */
  Invoice.prototype.save = function (options) {
    var self     = this;
    var url      = self.invoice.id ? '/invoice': 'invoices';
    var method   = self.invoice.id ? 'patch': 'post';

    return self.validate()
    .then(() => self.assemble())
    .then(invoice => workshopAPI[method](url, invoice, 'invoice'))
    .then(invoice => self.cleanup(invoice, options));
  };

  Invoice.prototype.split = function () {
    var self = this;
    var flag = true;

    angular.forEach(self.invoice.invoice_items_attributes, function (item) {
      flag = item.id ? flag: false;
    });

    return $q.when().then(function () {
      return flag ? $q.when(): confirm.generic($translate.instant('JS_SPACE.CONFIRM.SAVE_INVOICE_FIRST'));
    }).then(function () {
      return flag ? $q.when(): self.save();
    }).then(function () {
      return $uibModal.open({
        controller: 'SplitInvoiceCtrl',
        resolve   : {
          invoice: function () {
            return self.invoice;
          }
        },
        templateUrl: SplitInvoiceHtml,
        windowClass: 'x-lg-modal'
      }).result;
    }).then(function (data) {
      return $q.when(data);
    }, function (err) {
      return $q.reject(err);
    });
  };

  Invoice.prototype.status = function (status) {
    var self = this;
    var statusMap = {
      open     : 'O',
      closed   : 'C',
      processed: 'P'
    };

    if (status === 'actionable') {
      return (self.type('credit') && self.status('processed')) ||
        self.invoice.customer_id && self.type('quote') ||
        (self.invoice.customer_id && (self.invoice.repco || self.invoice.ashdown || self.invoice.prolink) );
    }

    return statusMap[status] === self.invoice.invoice_status;
  };

  /**
   * Determines if the invoice matches the specified
   * type
   * @param  {string} type    The type of invoice being checked
   * @return {boolean}        Is the invoice the specified type
   */
  Invoice.prototype.type = function (type) {
    var self = this;
    var typeMap = {
      credit  : 'C',
      quote   : 'Q',
      invoice : 'I',
      internal: 'U'
    };

    return typeMap[type] === self.invoice.invoice_type;
  };

  Invoice.prototype.updateUnitCost = function(row) {
    $uibModal.open({
      templateUrl: InvoiceUnitCostHtml,
      controller : 'UpdateUnitCostCtrl',
      windowClass: 'sm-modal',
      resolve    : {
        item: () => row.entity
      }
    })
    .result
    .then(result => {
      var company  = sessionObjAPI.company();
      var unitCost = bigDecimalApi.get(result.item.new_unit_cost).setScale(6, BigDecimal.ROUND_HALF_UP);
      var taxRate  = bigDecimalApi.get(company.sales_tax_rate).movePointLeft(2);

      if (company.price_includes_tax) {
        row.entity.cost_including_tax = result.item.new_unit_cost;
        row.entity.unit_cost = unitCost.divide(BigDecimal.ONE.add(taxRate)).setScale(2, BigDecimal.ROUND_HALF_UP).toString();
      }
      else {
        row.entity.unit_cost = result.item.new_unit_cost;
        row.entity.cost_including_tax = unitCost.multiply(BigDecimal.ONE.add(taxRate)).setScale(2, BigDecimal.ROUND_HALF_UP).toString();
      }
    });
  };

  /**
   * Handles all validation on the vendor invoice.
   * Validates the form and then throws a specialized error if it
   * is invalid
   * @return {promise} The invoice if valid, nothing if invalid
   */
  Invoice.prototype.validate = function () {
    var self = this;
    var deferred = $q.defer();

    if (isEmpty(self.form)) {
      return $q.when(self.invoice);
    }

    if (self.form.$valid) {
      deferred.resolve(self.invoice);
    }
    else {
      messages.clientError(self.form);
      deferred.reject();
    }

    return deferred.promise;
  };

  Invoice.prototype.validateAttachments = function (invoice) {
    var attachments = {};

    return workshopAPI.get('invoice/has_orders_attached', invoice.id).then(function (result) {
        attachments.openOrders = result;
        return workshopAPI.get('invoice/has_vendor_invoices_attached', invoice.id);
      }).then(function (result) {
        attachments.openInvoices = result;

        return attachments;
      }, function (err) {
        return err;
      });
  };

  Invoice.prototype.void = function() {
    var self = this;

    return workshopAPI.get('/invoices/void_invoice', self.invoice.id);
  };

  return Invoice;
}
